import type { ConfigProps, MenuItem } from '../../configTypes';
import { NavigationMenuItemFragment } from '../fragments/NavigationMenuItem';
import { FeaturedImageFragment } from '../fragments/FeaturedImage';
import { SeoPostFragment } from '../fragments/SEO';
import { TaxonomyFragment } from '../fragments/Taxonomy';
import { ContributorsFragment } from '../fragments/Contributors';
import { MoneyMetaFragment } from '../fragments/MoneyMeta';
import transformSiteSettings from '../utils/transformSiteSettings';
import { TransformedSeo } from '../transform/transformSEO';

import enrichContent from '../enrich/enrichContent';
import transformSeo from '../transform/transformSEO';
import executeGraphQlRequest from './executeGraphQlRequest';
import imageTransformationOnComponents from '../transform/imageTransformationOnComponents';
import transformTableMarkup from '../transform/transformTableMarkup';

interface PostPreviewNode {
  nodeById: {
    uri: string;
  };
  contentNode: Record<string, unknown>;
  generalSettings?: Record<string, string>;
  headerMenuItems: {
    nodes: MenuItem[];
  };
  footerMenuItems: {
    nodes: MenuItem[];
  };
  siteSettings?: Record<string, unknown>;
  auxiliaryMenuItems?: {
    nodes: MenuItem[];
  };
  enrichedContent?: Record<string, unknown> | null;
  transformedSEO?: TransformedSeo;
}

function previewQuery(config: ConfigProps): string {
  const { postTypeGql } = config;

  return `
    ${NavigationMenuItemFragment()}
    ${FeaturedImageFragment(postTypeGql)}
    ${SeoPostFragment(postTypeGql)}
    ${TaxonomyFragment(postTypeGql)}
    ${ContributorsFragment(postTypeGql)}
    ${MoneyMetaFragment(postTypeGql)}

    query PreviewNode(
      $id: ID!
      $headerMenuLocation: MenuLocationEnum
      $footerMenuLocation: MenuLocationEnum
      $secondaryMenuLocation: MenuLocationEnum
      $auxiliaryMenuLocation: MenuLocationEnum
    ) {
      __typename

      nodeById: contentNode(idType: DATABASE_ID, id: $id) {
        uri
      }

      contentNode(idType: DATABASE_ID, id: $id, asPreview: true) {
        ... on ${postTypeGql} {
          id
          databaseId
          title
          content
          date: customDate
          publishedDate: date
          modified
          pageTemplate
          customDateType
          uri
          additionalBylineDescription
          sidebarContent
          displayFeaturedImage
          topAdURL
          topMobileAdURL
          topDestinationURL
          sideAdURL
          sideDestinationURL
          topAdImageAlt
          sideAdImageAlt
          ...FeaturedImageFragment
          ...SeoPostFragment
          ...TaxonomyFragment
          ...MoneyMetaFragment
          ...ContributorsFragment
        }
      }

      generalSettings { title description }

      headerMenuItems: menuItems(where: { location: $headerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      footerMenuItems: menuItems(where: { location: $footerMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      secondaryMenuItems: menuItems(where: { location: $secondaryMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
      auxiliaryMenuItems: menuItems(where: { location: $auxiliaryMenuLocation }, first: 1000) {
        nodes { ...NavigationMenuItemFragment }
      }
    }
  `;
}

type FetchPostPreviewGraphQLData = {
  nodeById: { uri: string };
  contentNode: unknown;
  generalSettings?: { [key: string]: Record<string, unknown> };
  headerMenuItems?: { nodes: MenuItem[] };
  footerMenuItems?: { nodes: MenuItem[] };
  secondaryMenuItems?: { nodes: MenuItem[] };
  auxiliaryMenuItems?: { nodes: MenuItem[] };
  siteSettings?: Array<Record<string, unknown>>;
};

export async function fetchPostPreview(
  id: number | string | undefined,
  config: ConfigProps,
  accessToken: string,
): Promise<PostPreviewNode | null> {
  const {
    cmsUrl,
    headerMenuLocation,
    footerMenuLocation,
    secondaryMenuLocation,
    auxiliaryMenuLocation,
  } = config;

  if (!cmsUrl || !accessToken) return null;

  const query = previewQuery(config);
  const variables = {
    id,
    headerMenuLocation,
    footerMenuLocation,
    secondaryMenuLocation,
    auxiliaryMenuLocation,
  };

  let data: FetchPostPreviewGraphQLData | null = null;
  let lastError: unknown;

  try {
    data = await executeGraphQlRequest<FetchPostPreviewGraphQLData>({
      cmsUrl,
      query,
      variables,
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  } catch (err) {
    lastError = err;
  }

  const node = data?.contentNode ?? null;
  if (!node || typeof node !== 'object' || !data) {
    if (process.env['NODE_ENV'] === 'development') {
      console.warn(
        '[fetchPostPreview] No preview node found. id:',
        id,
        'cmsUrl:',
        cmsUrl,
        lastError ? `Error: ${lastError}` : 'contentNode was null',
      );
    }
    return null;
  }

  const nodeObj = node as Record<string, unknown>;
  const generalSettings = data.generalSettings;
  const normalizedGeneralSettings = generalSettings
    ? Object.fromEntries(
        Object.entries(generalSettings).map(([key, value]) => [
          key,
          value?.toString?.() ?? String(value ?? ''),
        ]),
      )
    : undefined;

  try {
    const enrichedContent = await enrichContent(
      nodeObj as {
        content?: string;
        contributors?: unknown;
        customDateType?: string;
        date?: string;
        additionalBylineDescription?: string;
        featuredImage?: unknown;
        seo?: { canonicalUrl?: string; openGraph?: { description?: string } };
        title?: string;
      },
      config,
    );

    const transformedContent = imageTransformationOnComponents(
      (nodeObj['content'] as string) || '',
    );
    nodeObj['content'] = transformTableMarkup(transformedContent);

    const rawSidebar = (nodeObj['sidebarContent'] as string) || '';
    if (rawSidebar) {
      nodeObj['sidebarContent'] = transformTableMarkup(
        imageTransformationOnComponents(rawSidebar),
      );
    }

    const transformedSEO = transformSeo({
      generalSettings: generalSettings as unknown as Record<string, string>,
      pageSeo: nodeObj['seo'] as Parameters<typeof transformSeo>[0]['pageSeo'],
      config,
      uri: (nodeObj['uri'] as string) || '',
    });

    const enrichedSiteSettings = transformSiteSettings(
      data.siteSettings?.[0],
      config,
    );

    return {
      ...data,
      generalSettings: normalizedGeneralSettings,
      siteSettings: enrichedSiteSettings,
      enrichedContent,
      transformedSEO,
    } as PostPreviewNode;
  } catch (error) {
    console.error('[fetchPostPreview]', id, error);
    return null;
  }
}

export default fetchPostPreview;
export type { PostPreviewNode };
